/**
 * Renewing a paid plan before it expires — what the renewal dialog shows.
 *
 * The backend decides (backend/subscription_renewal.py); these are the same
 * rules so the dates on screen are the ones the payment will produce:
 *   - paid while the period is still running → the new period starts on the
 *     current expiry date, no paid day is lost;
 *   - a different plan waits for that date, the running plan stays until then;
 *   - a lapsed or trial account starts today with the plan it pays for.
 */

export const CYCLES = ['monthly', 'yearly'];

// Set before leaving for the payment gateway, read on the way back: tells the
// plan page it is waiting for a renewal (the account is already active, so
// "active" alone proves nothing) and which expiry date it had before paying.
const RETURN_KEY = 'zb_renewal_return';
const RETURN_MAX_AGE_MS = 2 * 60 * 60 * 1000;

/** One billing period later, clamping the day (31 Jan + 1 month = 28/29 Feb). */
export function addPeriod(date, cycle) {
    const d = new Date(date);
    const day = d.getUTCDate();
    let year = d.getUTCFullYear();
    let month = d.getUTCMonth();
    if (cycle === 'monthly') {
        month += 1;
        if (month > 11) { month = 0; year += 1; }
    } else {
        year += 1;
    }
    const lastDay = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
    d.setUTCFullYear(year, month, Math.min(day, lastDay));
    return d;
}

/** True when the account is active and its paid period is still running. */
export function isEarlyRenewal(profile, now = new Date()) {
    const p = profile || {};
    if (p.subscription_status !== 'active' || p.account_status === 'suspended') return false;
    if (!p.next_billing_date) return false;
    const due = new Date(p.next_billing_date);
    return !Number.isNaN(due.getTime()) && due.getTime() > new Date(now).getTime();
}

/** { start, end, early } of the period a payment made now buys. */
export function renewalPeriod(profile, cycle, now = new Date()) {
    const early = isEarlyRenewal(profile, now);
    const start = early ? new Date(profile.next_billing_date) : new Date(now);
    return { start, end: addPeriod(start, cycle), early };
}

/** Gross amount charged for one cycle of a plan card (lib/plans.js). */
export function planAmount(plan, cycle) {
    if (!plan || plan.custom) return null;
    return cycle === 'yearly' ? plan.annualBilled : plan.priceMonthly;
}

const sameName = (a, b) => (a || '').localeCompare(b || '', 'es', { sensitivity: 'base' }) === 0;

/** The plans table row behind a plan card. Accent/case-insensitive, so
 *  "Plan Básico" matches a stored "Plan Basico". */
export function dbPlanFor(plan, dbPlans) {
    return (dbPlans || []).find(p => sameName(p.name, plan && plan.name)) || null;
}

/** The plan card behind a plans table row (null for a plan with no card). */
export function cardForDbPlan(dbPlan, plans) {
    if (!dbPlan) return null;
    return (plans || []).find(p => sameName(p.name, dbPlan.name)) || null;
}

/** Plan changes already paid for that wait for their start day, oldest first. */
export function pendingPlanChanges(rows) {
    return (rows || [])
        .filter(r => r && r.status === 'pending' && r.plan_id && r.ref_date)
        .sort((a, b) => String(a.ref_date).localeCompare(String(b.ref_date)));
}

export function rememberRenewal(profile, storage = safeStorage()) {
    if (!storage) return;
    try {
        storage.setItem(RETURN_KEY, JSON.stringify({
            owner: profile && profile.id,
            due: (profile && profile.next_billing_date) || null,
            at: Date.now(),
        }));
    } catch (e) { /* private mode: the page falls back to the plain check */ }
}

export function recallRenewal(ownerId, storage = safeStorage(), now = Date.now()) {
    if (!storage) return null;
    try {
        const saved = JSON.parse(storage.getItem(RETURN_KEY) || 'null');
        if (!saved || saved.owner !== ownerId) return null;
        if (now - Number(saved.at || 0) > RETURN_MAX_AGE_MS) return null;
        return saved;
    } catch (e) {
        return null;
    }
}

export function forgetRenewal(storage = safeStorage()) {
    if (!storage) return;
    try { storage.removeItem(RETURN_KEY); } catch (e) { /* ignore */ }
}

/** Whether the payment has been recorded: the expiry date moved forward. */
export function renewalRecorded(saved, profile) {
    if (!saved || !profile || !profile.next_billing_date) return false;
    if (!saved.due) return profile.subscription_status === 'active';
    return new Date(profile.next_billing_date).getTime() > new Date(saved.due).getTime();
}

function safeStorage() {
    try { return window.localStorage; } catch (e) { return null; }
}
